import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Actions,
  BrandHeaderRow,
  BrandLogo,
  Card,
  Header,
  HeaderIconButton,
  HeaderText,
  HomeIcon,
  Page,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';

const HISTORY_PAGE_SIZE = 10;
const HISTORY_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' }
];

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const HistoryPanel = styled(Card)`
  display: grid;
  gap: 0.85rem;
`;

const HistoryFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const HistoryFilterButton = styled.button`
  min-height: 38px;
  padding: 0.5rem 0.85rem;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(251, 191, 36, 0.72)' : 'rgba(255, 255, 255, 0.12)')};
  border-radius: 999px;
  background: ${({ $active }) => ($active ? 'rgba(251, 191, 36, 0.18)' : 'rgba(255, 255, 255, 0.08)')};
  color: ${({ $active }) => ($active ? '#fde68a' : '#f8fafc')};
  font: inherit;
  font-size: 0.9rem;
  font-weight: 900;
  cursor: pointer;
`;

const HistoryFilterCount = styled.span`
  color: ${({ $active }) => ($active ? '#fef3c7' : 'rgba(203, 213, 225, 0.72)')};
`;

const HistoryMoneyStat = styled.div`
  color: rgba(203, 213, 225, 0.78);
  font-size: 0.95rem;
  font-weight: 800;

  strong {
    color: #f8fafc;
  }
`;

const HistoryList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const HistoryCard = styled.article`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.1);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.045);

  @media (max-width: 680px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const HistoryText = styled.div`
  display: grid;
  gap: 0.35rem;
`;

const HistorySentence = styled.div`
  color: rgba(248, 250, 252, 0.9);
  font-weight: 900;
  line-height: 1.35;
`;

const HistoryMeta = styled.div`
  color: rgba(203, 213, 225, 0.62);
  font-size: 0.95rem;
  font-weight: 700;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0.45rem 0.75rem;
  border: 1px solid ${({ $failed }) => ($failed ? 'rgba(248, 113, 113, 0.32)' : 'rgba(74, 222, 128, 0.28)')};
  border-radius: 999px;
  background: ${({ $failed }) => ($failed ? 'rgba(248, 113, 113, 0.16)' : 'rgba(22, 101, 52, 0.28)')};
  color: ${({ $failed }) => ($failed ? '#fecaca' : '#bbf7d0')};
  font-size: 0.9rem;
  font-weight: 950;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  padding: 1.5rem;
  border: 1px dashed rgba(226, 232, 240, 0.28);
  border-radius: 24px;
  color: #cbd5e1;
  text-align: center;

  h2 {
    margin: 0;
    color: rgba(226, 232, 240, 0.78);
    font-size: 1.35rem;
    font-weight: 500;
  }
`;

const LoadMoreButton = styled.button`
  min-height: 44px;
  padding: 0.7rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: #f8fafc;
  font: inherit;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  padding: 0.8rem 1rem;
  border: 1px solid rgba(248, 113, 113, 0.24);
  border-radius: 18px;
  background: rgba(127, 29, 29, 0.2);
  color: #fecaca;
  font-weight: 800;
`;

const getApiUrl = () => {
  return window.RUNTIME_CONFIG?.apiUrl || process.env.REACT_APP_API_URL;
};

const formatDate = (time) => {
  return new Date(time).toLocaleDateString([], {
    month: 'short',
    day: 'numeric'
  });
};

const formatTime = (time) => {
  return new Date(time).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatMoney = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

const getSortedHistory = (items) => {
  return items
    .filter(alarm => !alarm.active)
    .sort((firstAlarm, secondAlarm) => new Date(secondAlarm.time) - new Date(firstAlarm.time));
};

const filterHistoryByStatus = (items, statusFilter) => {
  if (statusFilter === 'failed') {
    return items.filter(alarm => Boolean(alarm.failed));
  }

  if (statusFilter === 'completed') {
    return items.filter(alarm => !alarm.failed);
  }

  return items;
};

const getHistoryCounts = (items) => {
  const failed = items.filter(alarm => Boolean(alarm.failed)).length;
  const completed = items.length - failed;
  const totalPaid = items.reduce((total, alarm) => {
    const wasPaid = alarm.failed && alarm.punishmentType === 'monetary';

    return wasPaid ? total + Number(alarm.amount || 0) : total;
  }, 0);

  return {
    all: items.length,
    completed,
    failed,
    totalPaid
  };
};

const isRunPledge = (alarm) => alarm?.pledgeType === 'run';

const formatRunDistance = (alarm) => {
  const miles = Number(alarm?.metadata?.distanceMiles);

  if (!Number.isFinite(miles)) {
    return 'Run pledge';
  }

  return `Run ${miles.toFixed(2)} mi`;
};

const formatRunPace = (alarm) => {
  const pace = Number(alarm?.metadata?.paceMinutesPerMile);

  if (!Number.isFinite(pace)) {
    return 'Not set';
  }

  return `${pace.toFixed(pace % 1 === 0 ? 0 : 1)} min/mi`;
};

const formatRunDistanceSource = (alarm) => {
  return alarm?.metadata?.distanceSource === 'walking_route'
    ? 'Walking route'
    : 'Straight line';
};

const PledgeHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [historyAlarms, setHistoryAlarms] = useState([]);
  const [historyNextCursor, setHistoryNextCursor] = useState(null);
  const [historyFallbackItems, setHistoryFallbackItems] = useState(null);
  const [historyFallbackOffset, setHistoryFallbackOffset] = useState(0);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
  const [historyCounts, setHistoryCounts] = useState(null);

  const locationNameById = useMemo(() => {
    return locations.reduce((result, location) => ({
      ...result,
      [location.id]: location.name
    }), {});
  }, [locations]);

  const loadHistory = useCallback(async ({ reset = false, statusFilter = historyStatusFilter } = {}) => {
    if (historyLoading) {
      return;
    }

    if (reset) {
      setHistoryAlarms([]);
      setHistoryNextCursor(null);
      setHistoryFallbackItems(null);
      setHistoryFallbackOffset(0);
      setHistoryLoaded(false);
    }

    if (!reset && historyFallbackItems) {
      const nextOffset = historyFallbackOffset + HISTORY_PAGE_SIZE;
      setHistoryAlarms(historyFallbackItems.slice(0, nextOffset));
      setHistoryFallbackOffset(nextOffset);
      setHistoryNextCursor(nextOffset < historyFallbackItems.length ? String(nextOffset) : null);
      return;
    }

    setHistoryLoading(true);
    setHistoryError('');

    try {
      const response = await axios.get(`${getApiUrl()}/alarms`, {
        params: {
          active: false,
          limit: HISTORY_PAGE_SIZE,
          includeCounts: true,
          ...(statusFilter === 'all' ? {} : { failed: statusFilter === 'failed' }),
          ...(reset || !historyNextCursor ? {} : { cursor: historyNextCursor })
        },
        headers: { Authorization: `Bearer ${await user.getIdToken()}` }
      });
      const payload = response.data;

      if (Array.isArray(payload)) {
        const sortedHistory = getSortedHistory(payload);
        const filteredHistory = filterHistoryByStatus(sortedHistory, statusFilter);

        setHistoryCounts(getHistoryCounts(sortedHistory));
        setHistoryFallbackItems(filteredHistory);
        setHistoryFallbackOffset(HISTORY_PAGE_SIZE);
        setHistoryAlarms(filteredHistory.slice(0, HISTORY_PAGE_SIZE));
        setHistoryNextCursor(filteredHistory.length > HISTORY_PAGE_SIZE ? String(HISTORY_PAGE_SIZE) : null);
        setHistoryLoaded(true);
        return;
      }

      const { items = [], nextCursor = null, counts = null } = payload;

      if (counts) {
        setHistoryCounts(counts);
      }

      setHistoryAlarms(prev => (reset ? items : [...prev, ...items]));
      setHistoryNextCursor(nextCursor);
      setHistoryLoaded(true);
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistoryError('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }, [
    historyFallbackItems,
    historyFallbackOffset,
    historyLoading,
    historyNextCursor,
    historyStatusFilter,
    user
  ]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${getApiUrl()}/locations`, {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        });

        setLocations(response.data);
      } catch (error) {
        console.error('Error fetching history locations:', error);
      }
    };

    fetchLocations();
  }, [user]);

  useEffect(() => {
    loadHistory({ reset: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHistoryFilterChange = (nextFilter) => {
    setHistoryStatusFilter(nextFilter);
    loadHistory({ reset: true, statusFilter: nextFilter });
  };

  return (
    <Page>
      <Shell>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Home" onClick={() => navigate('/dashboard')}>
                  <HomeIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>History.</Title>
            <Subtitle>Review completed and failed pledges.</Subtitle>
          </HeaderText>
        </Header>

        <HistoryPanel>
          <HistoryFilters aria-label="History filter">
            {HISTORY_FILTERS.map((filter) => (
              <HistoryFilterButton
                key={filter.value}
                type="button"
                $active={historyStatusFilter === filter.value}
                aria-pressed={historyStatusFilter === filter.value}
                onClick={() => handleHistoryFilterChange(filter.value)}
              >
                {filter.label} <HistoryFilterCount $active={historyStatusFilter === filter.value}>{historyCounts?.[filter.value] ?? '-'}</HistoryFilterCount>
              </HistoryFilterButton>
            ))}
          </HistoryFilters>

          {historyCounts?.totalPaid > 0 && (
            <HistoryMoneyStat>
              Total paid over time: <strong>{formatMoney(historyCounts.totalPaid)}</strong>
            </HistoryMoneyStat>
          )}

          <HistoryList id="pledge-history">
            {historyLoading && !historyLoaded && (
              <EmptyState>
                <h2>Loading history...</h2>
              </EmptyState>
            )}

            {historyError && <Message>{historyError}</Message>}

            {historyLoaded && historyAlarms.length === 0 && (
              <EmptyState>
                <h2>No {historyStatusFilter === 'all' ? 'pledge' : historyStatusFilter} history yet.</h2>
              </EmptyState>
            )}

            {historyAlarms.map((alarm) => {
              const runPledge = isRunPledge(alarm);

              return (
                <HistoryCard key={alarm.id}>
                  <HistoryText>
                    <HistorySentence>
                      {runPledge
                        ? `${formatRunDistance(alarm)} by ${formatDate(alarm.time)} ${formatTime(alarm.time)}`
                        : `I pledged to be at ${locationNameById[alarm.locationId] || 'a saved place'} by ${formatDate(alarm.time)} ${formatTime(alarm.time)}`}
                    </HistorySentence>
                    <HistoryMeta>
                      {runPledge && `Pace: ${formatRunPace(alarm)}. Distance: ${formatRunDistanceSource(alarm)}. `}
                      {alarm.punishmentType === 'email'
                        ? 'Consequence: accountability email'
                        : `Consequence: ${formatMoney(alarm.amount)}`}
                    </HistoryMeta>
                  </HistoryText>
                  <StatusPill $failed={Boolean(alarm.failed)}>
                    {alarm.failed ? 'Failed' : 'Completed'}
                  </StatusPill>
                </HistoryCard>
              );
            })}

            {historyLoaded && historyNextCursor && (
              <LoadMoreButton type="button" onClick={() => loadHistory()} disabled={historyLoading}>
                {historyLoading ? 'Loading...' : 'Load more'}
              </LoadMoreButton>
            )}
          </HistoryList>
        </HistoryPanel>
      </Shell>
    </Page>
  );
};

export default PledgeHistory;
